import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash, Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function ApiKeys() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const create = async () => {
    try {
      const { data } = await api.post("/api-keys", { name });
      setNewKey(data.key);
      setName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/api-keys/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success(t("apiKeys.copied")); };

  const closeDialog = (o) => { setOpen(o); if (!o) setNewKey(null); };

  return (
    <div>
      <PageHeader
        title={t("apiKeys.title")}
        subtitle={t("apiKeys.subtitle")}
        action={
          <Dialog open={open} onOpenChange={closeDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="create-key-button">
                <Plus size={16} className="mr-2" /> {t("apiKeys.create")}
              </Button>
            </DialogTrigger>
            <DialogContent className="backdrop-blur-xl bg-background/80">
              <DialogHeader><DialogTitle className="font-heading">{t("apiKeys.createTitle")}</DialogTitle><DialogDescription className="sr-only">Create an API key</DialogDescription></DialogHeader>
              {newKey ? (
                <div className="space-y-3">
                  <p className="text-sm text-destructive font-medium">{t("apiKeys.keyOnce")}</p>
                  <div className="flex items-center gap-2 bg-secondary p-3 rounded-sm">
                    <code className="font-mono text-xs break-all flex-1" data-testid="new-api-key-value">{newKey}</code>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copy(newKey)} data-testid="copy-key-button"><Copy size={16} /></Button>
                  </div>
                  <DialogFooter><Button onClick={() => closeDialog(false)}>{t("common.close")}</Button></DialogFooter>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>{t("common.name")}</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="key-name-input" />
                  </div>
                  <DialogFooter><Button onClick={create} disabled={!name.trim()} data-testid="create-key-submit">{t("common.create")}</Button></DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("apiKeys.prefix")}</TableHead>
              <TableHead>{t("common.created")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((k) => (
              <TableRow key={k.id} data-testid={`apikey-row-${k.id}`}>
                <TableCell className="font-medium">{k.name}</TableCell>
                <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{k.created_at.slice(0, 10)}</TableCell>
                <TableCell>
                  {k.revoked
                    ? <Badge variant="secondary" className="rounded-sm text-[10px] uppercase">{t("apiKeys.revoked")}</Badge>
                    : <Badge className="rounded-sm text-[10px] uppercase"><Check size={10} className="mr-1" />active</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {!k.revoked && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => revoke(k.id)} data-testid={`revoke-key-${k.id}`}>
                      <Trash size={16} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
